import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShoppingBag, Search, Plus, Loader2, Download, Trash2, ShieldCheck, Tag, Store, PackageOpen, Users,
  Heart, Star, MessageSquare, Flag, Sparkles, Gift,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReportDialog } from "@/components/ReportDialog";
import GiftReveal from "@/components/GiftReveal";
import { PageHeader, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SELLER_ROLES = ["creator", "educator", "admin", "superadmin"];
const KIND_LABEL = { character: "Character", game: "World", asset: "Asset" };
const KIND_EMOJI = { character: "🧍", game: "🌍", asset: "✨" };
const CATEGORIES = [
  ["all", "All", "🛍️"], ["character", "Characters", "🧍"], ["world", "Worlds", "🌍"],
  ["skin", "Skins", "🎽"], ["texture", "Textures", "🧩"], ["prop", "Props", "📦"],
  ["sound", "Sounds", "🎵"], ["kit", "Starter Kits", "🚀"], ["other", "Other", "✨"],
];
const ASSET_CATEGORIES = [["skin", "Skin"], ["texture", "Texture"], ["prop", "Prop"], ["sound", "Sound"], ["kit", "Starter Kit"], ["other", "Other"]];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(([k, l]) => [k, l]));
const LICENSES = {
  all_rights_reserved: "All rights reserved",
  personal: "Personal use only",
  commercial: "Commercial use allowed",
  cc0: "CC0 — Public domain",
};
const priceLabel = (p) => (p > 0 ? `$${Number(p).toFixed(2)}` : "Free");

export default function Marketplace() {
  const { user } = useAuth();
  const canSell = SELLER_ROLES.includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = params.get("tab");
    return ["browse", "wishlist", "purchases", "mine"].includes(t) ? t : "browse";
  });
  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("new");
  const [busyId, setBusyId] = useState(null);
  const [showList, setShowList] = useState(false);
  const [store, setStore] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [detail, setDetail] = useState(null);
  const [appealListing, setAppealListing] = useState(null);
  const [appealMsg, setAppealMsg] = useState("");

  const loadBrowse = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (kind !== "all") p.set("kind", kind);
      if (category !== "all") p.set("category", category);
      p.set("sort", sort);
      const { data } = await api.get(`/marketplace?${p.toString()}`);
      setListings(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, kind, category, sort]);

  const loadStore = useCallback(async () => {
    try { const { data } = await api.get("/marketplace/storefront"); setStore(data); }
    catch { setStore(null); }
  }, []);

  const loadMine = useCallback(async () => {
    const { data } = await api.get("/marketplace/mine");
    setMine(data);
  }, []);
  const loadPurchases = useCallback(async () => {
    const { data } = await api.get("/marketplace/purchases");
    setPurchases(data);
  }, []);
  const loadWishlist = useCallback(async () => {
    try { const { data } = await api.get("/marketplace/wishlist"); setWishlist(data); }
    catch { setWishlist([]); }
  }, []);

  useEffect(() => { if (tab === "browse") { loadBrowse(); loadStore(); } }, [tab, loadBrowse, loadStore]);
  useEffect(() => { if (tab === "mine") loadMine(); }, [tab, loadMine]);
  useEffect(() => { if (tab === "purchases") loadPurchases(); }, [tab, loadPurchases]);
  useEffect(() => { if (tab === "wishlist") loadWishlist(); }, [tab, loadWishlist]);

  // keep wishlist flags in sync across all loaded lists
  const applyWishFlag = useCallback((id, wishlisted, count) => {
    const patch = (arr) => arr.map((l) => l.id === id ? { ...l, wishlisted, ...(count != null ? { wishlist_count: count } : {}) } : l);
    setListings(patch);
    setStore((s) => s ? { ...s, rails: s.rails.map((r) => ({ ...r, items: patch(r.items) })) } : s);
    setDetail((d) => d && d.id === id ? { ...d, wishlisted, ...(count != null ? { wishlist_count: count } : {}) } : d);
  }, []);

  const toggleWishlist = useCallback(async (l) => {
    try {
      const { data } = await api.post(`/marketplace/${l.id}/wishlist`);
      applyWishFlag(l.id, data.wishlisted, data.wishlist_count);
      toast.success(data.wishlisted ? "Added to wishlist ❤️" : "Removed from wishlist");
      if (tab === "wishlist") loadWishlist();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [tab, loadWishlist, applyWishFlag]);

  // Stripe return handler
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/marketplace/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(`Purchased “${data.listing_title}” — added to your library! 🎉`);
          params.delete("session_id"); setParams(params, { replace: true });
          setTab("purchases"); loadPurchases();
          return;
        }
        if (data.status === "expired" || tries > 8) {
          toast.error("Payment didn't complete. Please try again.");
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        tries += 1; setTimeout(poll, 1500);
      } catch (e) {
        params.delete("session_id"); setParams(params, { replace: true });
      }
    };
    toast.info("Confirming your payment…");
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link from a creator profile: /app/marketplace?listing=<id> opens that listing's detail.
  useEffect(() => {
    const lid = params.get("listing");
    if (!lid) return;
    (async () => {
      try {
        const { data } = await api.get(`/marketplace/${lid}`);
        setDetail(data);
      } catch (e) { toast.error("That listing is no longer available."); }
      finally { params.delete("listing"); setParams(params, { replace: true }); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acquire = async (l) => {
    setBusyId(l.id);
    try {
      if (l.price > 0) {
        const { data } = await api.post(`/marketplace/${l.id}/checkout`, { origin_url: window.location.origin });
        window.location.href = data.url;
        return;
      }
      const { data } = await api.post(`/marketplace/${l.id}/acquire`);
      toast.success(data.message);
      loadBrowse();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const removeListing = async (id) => {
    setBusyId(id);
    try { await api.delete(`/marketplace/${id}`); toast.success("Listing removed"); loadMine(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const submitListingAppeal = async () => {
    if (!appealListing) return;
    setBusyId(appealListing.id);
    try {
      await api.post("/rights/appeal", { target_type: "listing", target_id: appealListing.id, message: appealMsg });
      toast.success("Appeal submitted — an admin will re-review your listing.");
      setAppealListing(null); setAppealMsg(""); loadMine();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="marketplace-page">
      <PageHeader title="Marketplace" icon={ShoppingBag}
        subtitle="Discover and acquire characters, worlds and assets from creators — or sell your own."
        action={canSell && (
          <Button onClick={() => setShowList(true)} className="rounded-full" data-testid="open-list-dialog">
            <Plus className="w-4 h-4" /> List an item
          </Button>
        )} />

      <Tabs value={tab} onValueChange={setTab}>
        {tab === "purchases" && <GiftReveal onClaimed={loadPurchases} />}
        <TabsList>
          <TabsTrigger value="browse" data-testid="tab-browse"><Store className="w-4 h-4 mr-1.5" /> Browse</TabsTrigger>
          <TabsTrigger value="wishlist" data-testid="tab-wishlist"><Heart className="w-4 h-4 mr-1.5" /> Wishlist</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases"><PackageOpen className="w-4 h-4 mr-1.5" /> My Library</TabsTrigger>
          {canSell && <TabsTrigger value="mine" data-testid="tab-mine"><Tag className="w-4 h-4 mr-1.5" /> My Listings</TabsTrigger>}
        </TabsList>

        <TabsContent value="browse" className="mt-5">
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the marketplace…"
                className="pl-9" data-testid="market-search" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-40" data-testid="market-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Newest</SelectItem>
                <SelectItem value="popular">Most popular</SelectItem>
                <SelectItem value="price_low">Price: low to high</SelectItem>
                <SelectItem value="price_high">Price: high to low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Category chips */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2" data-testid="market-categories">
            {CATEGORIES.map(([key, label, emoji]) => (
              <button key={key} onClick={() => setCategory(key)} data-testid={`market-cat-${key}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${category === key ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))]" : "bg-[hsl(var(--secondary))]"}`}>
                <span>{emoji}</span> {label}
                {store?.category_counts?.[key] > 0 && key !== "all" && <span className="opacity-60 text-xs">{store.category_counts[key]}</span>}
              </button>
            ))}
          </div>

          {/* Storefront rails (hidden while searching/filtering) */}
          {!q && category === "all" && store?.rails?.some((r) => r.items.length > 0) && (
            <div className="mt-3 mb-2" data-testid="storefront-rails">
              {store.rails.map((r) => r.items.length > 0 && (
                <div key={r.key} className="mb-7" data-testid={`store-rail-${r.key}`}>
                  <div className="mb-3"><h2 className="font-display text-xl font-bold">{r.title}</h2><p className="text-xs text-[hsl(var(--muted-foreground))]">{r.subtitle}</p></div>
                  <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                    {r.items.map((l) => (
                      <div key={l.id} className="w-44 shrink-0 snap-start">
                        <ListingCard l={l} busy={busyId === l.id} onAcquire={() => acquire(l)} onWishlist={() => toggleWishlist(l)} onOpen={() => setDetail(l)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <h2 className="font-display text-xl font-bold mb-3">🌍 Browse all</h2>
            </div>
          )}

          {loading ? <Loading /> : listings.length === 0 ? (
            <EmptyState emoji="🛍️" title="No items here yet" text="Try another category, or be the first creator to list one." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="market-grid">
              {listings.map((l) => (
                <ListingCard key={l.id} l={l} busy={busyId === l.id} onAcquire={() => acquire(l)} onWishlist={() => toggleWishlist(l)} onOpen={() => setDetail(l)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-5">
          {wishlist.length === 0 ? (
            <EmptyState emoji="❤️" title="Your wishlist is empty" text="Tap the heart on any listing to save it here for later." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="wishlist-grid">
              {wishlist.map((l) => (
                <ListingCard key={l.id} l={l} busy={busyId === l.id} onAcquire={() => acquire(l)} onWishlist={() => toggleWishlist(l)} onOpen={() => setDetail(l)} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="purchases" className="mt-5">
          {purchases.length === 0 ? (
            <EmptyState emoji="📦" title="Your library is empty" text="Items you acquire from the Marketplace will appear here and in your Studio." />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="purchases-grid">
              {purchases.map((p) => (
                <div key={p.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid={`purchase-${p.id}`}>
                  <div className="text-4xl mb-2">{p.preview_emoji || KIND_EMOJI[p.kind]}</div>
                  <Badge variant="secondary" className="mb-1.5">{KIND_LABEL[p.kind]}</Badge>
                  <h3 className="font-bold truncate">{p.title}</h3>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{priceLabel(p.price)} · owned</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {canSell && (
          <TabsContent value="mine" className="mt-5">
            {mine.length === 0 ? (
              <EmptyState emoji="🏷️" title="No listings yet" text="List one of your characters, worlds or assets to share or sell."
                action={<Button onClick={() => setShowList(true)} className="rounded-full" data-testid="empty-list-btn"><Plus className="w-4 h-4" /> List an item</Button>} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="mine-grid">
                {mine.map((l) => (
                  <div key={l.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 relative" data-testid={`mine-${l.id}`}>
                    {l.status === "removed" && <Badge variant="destructive" className="absolute top-3 right-3 text-[10px]">Removed</Badge>}
                    {l.status === "appealed" && <Badge className="absolute top-3 right-3 text-[10px] bg-amber-500/90">⏳ Appeal in review</Badge>}
                    <div className="text-4xl mb-2">{l.preview_emoji}</div>
                    <Badge variant="secondary" className="mb-1.5">{KIND_LABEL[l.kind]}</Badge>
                    <h3 className="font-bold truncate">{l.title}</h3>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-2">
                      <span>{priceLabel(l.price)}</span>·<span>{l.sales} sold</span>
                    </div>
                    {l.status === "active" && (
                      <Button variant="ghost" size="sm" disabled={busyId === l.id} onClick={() => removeListing(l.id)}
                        className="mt-3 text-[hsl(var(--destructive))] px-0 h-auto" data-testid={`remove-listing-${l.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                      </Button>
                    )}
                    {l.status === "removed" && (
                      <Button variant="ghost" size="sm" onClick={() => { setAppealListing(l); setAppealMsg(""); }}
                        className="mt-3 text-amber-500 px-0 h-auto" data-testid={`appeal-listing-${l.id}`}>
                        <Flag className="w-3.5 h-3.5 mr-1" /> Appeal removal
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {canSell && <ListItemDialog open={showList} onClose={() => setShowList(false)} onCreated={() => { setShowList(false); setTab("mine"); loadMine(); }} />}
      <ListingDetailDialog listing={detail} onClose={() => setDetail(null)}
        onAcquire={() => detail && acquire(detail)} onWishlist={() => detail && toggleWishlist(detail)} busy={busyId === detail?.id} />

      <Dialog open={!!appealListing} onOpenChange={(o) => !o && setAppealListing(null)}>
        <DialogContent data-testid="listing-appeal-dialog">
          <DialogHeader>
            <DialogTitle>Appeal removal of “{appealListing?.title}”</DialogTitle>
            <DialogDescription>This listing was removed by moderation. Tell us why it should be restored (e.g. it's your original work). An admin will re-review it.</DialogDescription>
          </DialogHeader>
          <textarea value={appealMsg} onChange={(e) => setAppealMsg(e.target.value)} maxLength={500} rows={4}
            placeholder="Explain why this is original content you have the rights to…"
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm resize-none" data-testid="listing-appeal-message" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAppealListing(null)} data-testid="listing-appeal-cancel">Cancel</Button>
            <Button onClick={submitListingAppeal} disabled={busyId === appealListing?.id} className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="listing-appeal-submit">
              <Flag className="w-4 h-4" /> Submit appeal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Loading() {
  return <div className="py-16 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[hsl(var(--primary))]" /></div>;
}

function Stars({ value = 0, size = "w-3 h-3", testid }) {
  const v = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= v ? "fill-amber-400 text-amber-400" : "text-[hsl(var(--muted-foreground))]/40"}`} />
      ))}
    </span>
  );
}

function ListingCard({ l, busy, onAcquire, onWishlist, onOpen }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover flex flex-col relative" data-testid={`listing-${l.id}`}>
      <button type="button" onClick={onOpen} className="h-28 grid place-items-center bg-gradient-to-br from-[#0b1026] to-[#1f6feb]/40 relative w-full text-left" data-testid={`open-listing-${l.id}`}>
        {l.preview_image ? (
          <img src={`${BACKEND}${l.preview_image}`} alt={l.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : null}
        <span className="text-5xl drop-shadow-lg relative">{l.preview_emoji || KIND_EMOJI[l.kind]}</span>
        <Badge className="absolute top-2 left-2 text-[10px]">{CAT_LABEL[l.category] || KIND_LABEL[l.kind]}</Badge>
      </button>
      {!l.mine && onWishlist && (
        <button type="button" onClick={onWishlist} data-testid={`wishlist-${l.id}`}
          className="absolute top-2 right-2 w-8 h-8 rounded-full grid place-items-center bg-black/40 backdrop-blur hover:bg-black/60 transition-colors"
          aria-label={l.wishlisted ? "Remove from wishlist" : "Add to wishlist"}>
          <Heart className={`w-4 h-4 transition-all ${l.wishlisted ? "fill-rose-500 text-rose-500 scale-110" : "text-white"}`} />
        </button>
      )}
      <div className="p-3.5 flex flex-col flex-1">
        <h3 className="font-bold truncate cursor-pointer hover:text-[hsl(var(--primary))]" title={l.title} onClick={onOpen}>{l.title}</h3>
        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {l.seller_name}</div>
        <div className="flex items-center gap-1.5 mt-1.5 min-h-[16px]">
          {l.rating_count > 0 ? (
            <><Stars value={l.rating_avg} testid={`card-stars-${l.id}`} />
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{Number(l.rating_avg).toFixed(1)} ({l.rating_count})</span></>
          ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">No reviews yet</span>}
        </div>
        {l.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-2">{l.description}</p>}
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {l.origin === "ai" && <Badge className="text-[9px] gap-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0" data-testid={`ai-built-${l.id}`}><Sparkles className="w-2.5 h-2.5" /> AI-built</Badge>}
          <Badge variant="outline" className="text-[9px] capitalize gap-1"><ShieldCheck className="w-2.5 h-2.5" /> {l.origin}</Badge>
          <Badge variant="outline" className="text-[9px]">{LICENSES[l.license]?.split(" ")[0] || l.license}</Badge>
          {l.revshare_with > 0 && <Badge variant="outline" className="text-[9px] gap-1"><Users className="w-2.5 h-2.5" /> Revenue split</Badge>}
        </div>
        <div className="flex items-center justify-between mt-auto pt-3">
          <span className="font-bold text-sm">{priceLabel(l.price)}</span>
          {l.mine ? (
            <Badge variant="secondary" className="text-[10px]">Your listing</Badge>
          ) : l.owned ? (
            <Badge variant="secondary" className="text-[10px]">✓ Owned</Badge>
          ) : (
            <Button size="sm" disabled={busy} onClick={onAcquire} className="rounded-full h-8" data-testid={`acquire-${l.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> {l.price > 0 ? "Buy" : "Get"}</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ListItemDialog({ open, onClose, onCreated }) {
  const [register, setRegister] = useState([]);
  const [kind, setKind] = useState("character");
  const [itemId, setItemId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("0");
  const [license, setLicense] = useState("personal");
  const [category, setCategory] = useState("skin");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/rights").then((r) => setRegister(r.data.items || [])).catch(() => {});
    setKind("character"); setItemId(""); setTitle(""); setDescription(""); setPrice("0"); setLicense("personal"); setCategory("skin");
  }, [open]);

  const options = register.filter((r) => r.kind === kind);

  const pick = (id) => {
    setItemId(id);
    const it = register.find((r) => r.id === id);
    if (it && !title) setTitle(it.name);
  };

  const submit = async () => {
    if (!itemId) { toast.error("Pick one of your items to list."); return; }
    setSaving(true);
    try {
      await api.post("/marketplace", {
        kind, item_id: itemId, title: title.trim(),
        description: description.trim(), price: parseFloat(price) || 0, license,
        category: kind === "asset" ? category : undefined,
        tags: [],
      });
      toast.success("Your item is now on the Marketplace! 🛍️");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="list-item-dialog">
        <DialogHeader>
          <DialogTitle>List an item</DialogTitle>
          <DialogDescription>Share or sell one of your creations. Items you own only — keep names original.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Type</label>
              <Select value={kind} onValueChange={(v) => { setKind(v); setItemId(""); }}>
                <SelectTrigger className="mt-1" data-testid="list-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="character">Character</SelectItem>
                  <SelectItem value="game">World (game)</SelectItem>
                  <SelectItem value="asset">Asset</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Item</label>
              <Select value={itemId} onValueChange={pick}>
                <SelectTrigger className="mt-1" data-testid="list-item"><SelectValue placeholder="Pick one…" /></SelectTrigger>
                <SelectContent>
                  {options.length === 0 ? <div className="px-2 py-1.5 text-xs text-[hsl(var(--muted-foreground))]">No {KIND_LABEL[kind]?.toLowerCase()}s yet</div>
                    : options.map((o) => <SelectItem key={o.id} value={o.id}>{o.emoji ? `${o.emoji} ` : ""}{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {kind === "asset" && (
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Asset category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="list-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Choose how shoppers find it — skins, textures, props, sounds or a starter kit.</p>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" maxLength={80}
              placeholder="e.g. Crystal Fox Hero" data-testid="list-title" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" rows={4}
              maxLength={2000} placeholder="What makes it great? You can write a detailed description (up to 2000 characters)." data-testid="list-desc" />
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 text-right">{description.length}/2000</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Price (USD, 0 = free)</label>
              <Input type="number" min="0" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)}
                className="mt-1" data-testid="list-price" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">License</label>
              <Select value={license} onValueChange={setLicense}>
                <SelectTrigger className="mt-1" data-testid="list-license"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(LICENSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="list-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish listing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ListingDetailDialog({ listing, onClose, onAcquire, onWishlist, busy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [giftOn, setGiftOn] = useState(false);
  const [giftTo, setGiftTo] = useState("");
  const [giftNote, setGiftNote] = useState("");
  const [gifting, setGifting] = useState(false);

  const load = useCallback(async () => {
    if (!listing) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/marketplace/${listing.id}/reviews`);
      setData(data);
      if (data.my_review) { setRating(data.my_review.rating); setComment(data.my_review.comment || ""); }
      else { setRating(0); setComment(""); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [listing]);

  useEffect(() => { if (listing) load(); }, [listing, load]);

  const submitReview = async () => {
    if (!rating) { toast.error("Pick a star rating first."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/marketplace/${listing.id}/reviews`, { rating, comment });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteReview = async () => {
    setSaving(true);
    try { await api.delete(`/marketplace/${listing.id}/reviews`); setRating(0); setComment(""); toast.success("Review removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const giftItem = async () => {
    if (!giftTo.trim()) { toast.error("Enter your friend's email or username."); return; }
    setGifting(true);
    try {
      const body = { gift: true, recipient: giftTo.trim(), note: giftNote.trim() };
      if (listing.price > 0) {
        const { data } = await api.post(`/marketplace/${listing.id}/checkout`, { origin_url: window.location.origin, ...body });
        window.location.href = data.url; return;
      }
      const { data } = await api.post(`/marketplace/${listing.id}/acquire`, body);
      toast.success(data.message || "Gift sent! 🎁");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGifting(false); }
  };

  if (!listing) return null;
  const summary = data?.summary || { avg: 0, count: 0, distribution: {} };
  const l = listing;

  return (
    <Dialog open={!!listing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="listing-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">{l.preview_emoji || KIND_EMOJI[l.kind]}</span> {l.title}
          </DialogTitle>
          <DialogDescription>by {l.seller_name} · {CAT_LABEL[l.category] || KIND_LABEL[l.kind]} · {priceLabel(l.price)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {l.description && <p className="text-sm text-[hsl(var(--muted-foreground))]">{l.description}</p>}

          <div className="flex items-center gap-3 flex-wrap">
            {!l.mine && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={onWishlist} data-testid="detail-wishlist">
                <Heart className={`w-4 h-4 mr-1 ${l.wishlisted ? "fill-rose-500 text-rose-500" : ""}`} />
                {l.wishlisted ? "Wishlisted" : "Add to wishlist"}
              </Button>
            )}
            {!l.mine && !l.owned && (
              <Button size="sm" className="rounded-full" disabled={busy} onClick={onAcquire} data-testid="detail-acquire">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" /> {l.price > 0 ? `Buy ${priceLabel(l.price)}` : "Get free"}</>}
              </Button>
            )}
            {l.owned && <Badge variant="secondary">✓ In your library</Badge>}
            {!l.mine && (
              <ReportDialog targetType="listing" targetId={l.id} targetName={l.title}
                trigger={<Button variant="ghost" size="sm" className="rounded-full text-[hsl(var(--muted-foreground))]" data-testid="detail-report"><Flag className="w-4 h-4 mr-1" /> Report</Button>} />
            )}
          </div>

          {/* Gift this single item to a friend */}
          {!l.mine && (
            <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/[0.05] p-3" data-testid="gift-item-section">
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-fuchsia-400" /> Gift this to a friend</span>
                <Switch checked={giftOn} onCheckedChange={setGiftOn} data-testid="gift-item-toggle" />
              </label>
              {giftOn && (
                <div className="mt-3 space-y-2">
                  <Input value={giftTo} onChange={(e) => setGiftTo(e.target.value)} data-testid="gift-item-recipient"
                    placeholder="Friend's email or username" className="rounded-xl" />
                  <Textarea value={giftNote} onChange={(e) => setGiftNote(e.target.value)} maxLength={240} data-testid="gift-item-note"
                    placeholder="Add a personal note (optional)…" className="rounded-xl min-h-[54px]" />
                  <Button onClick={giftItem} disabled={gifting} data-testid="gift-item-send"
                    className="w-full rounded-full bg-gradient-to-r from-fuchsia-500 to-[#3b9eff] font-bold">
                    {gifting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} {l.price > 0 ? `Gift it — ${priceLabel(l.price)}` : "Gift it — Free"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Reviews summary */}
          <div className="rounded-xl border border-[hsl(var(--border))] p-4">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold" data-testid="detail-avg-rating">{Number(summary.avg).toFixed(1)}</div>
                <Stars value={summary.avg} size="w-3.5 h-3.5" />
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{summary.count} review{summary.count === 1 ? "" : "s"}</div>
              </div>
              <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = summary.distribution?.[String(n)] || 0;
                  const pct = summary.count ? (c / summary.count) * 100 : 0;
                  return (
                    <div key={n} className="flex items-center gap-2 text-[11px]">
                      <span className="w-3 text-[hsl(var(--muted-foreground))]">{n}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                        <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-5 text-right text-[hsl(var(--muted-foreground))]">{c}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Write a review */}
          {data?.can_review && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4" data-testid="review-form">
              <div className="text-sm font-semibold mb-2">{data.my_review ? "Edit your review" : "Write a review"}</div>
              <div className="flex items-center gap-1 mb-2" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onMouseEnter={() => setHover(n)} onClick={() => setRating(n)} data-testid={`rate-star-${n}`}>
                    <Star className={`w-6 h-6 transition-transform hover:scale-110 ${n <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-[hsl(var(--muted-foreground))]/40"}`} />
                  </button>
                ))}
              </div>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000}
                placeholder="Share what you think (optional)…" className="text-sm" data-testid="review-comment" />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" className="rounded-full" disabled={saving} onClick={submitReview} data-testid="submit-review">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (data.my_review ? "Update review" : "Post review")}
                </Button>
                {data.my_review && <Button size="sm" variant="ghost" className="text-[hsl(var(--destructive))]" disabled={saving} onClick={deleteReview} data-testid="delete-review">Delete</Button>}
              </div>
            </div>
          )}

          {/* Reviews list */}
          <div className="space-y-3" data-testid="reviews-list">
            {loading ? <Loading /> : (data?.reviews?.length ? data.reviews.map((r) => (
              <div key={r.id} className="border-b border-[hsl(var(--border))] pb-3 last:border-0" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  {r.verified && <Badge variant="outline" className="text-[9px] gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Verified buyer</Badge>}
                  <Stars value={r.rating} size="w-3 h-3" />
                </div>
                {r.comment && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{r.comment}</p>}
              </div>
            )) : <div className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Be the first to review this.</div>)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
